import { Screen, ScreenConfig } from '../core/Screen';
import { APIManager, StateManager } from '../../managers';
import { renderUnitPortrait } from '../components/UnitPortrait';
import { renderStrandShowcase } from '../components/StrandShowcase';
import { renderMasteryIcon } from '../components/ItemIcon';
import { rarityColor } from '../components/TooltipContent';
import { parseUnitAffixes, formatAffixValue, affixTypeClass } from '../components/AffixDisplay';
import { getTierLabel } from '../components/TooltipContent';
import { screenHeaderHTML, renderScreenCurrencies } from '../components/ScreenHeader';
import { formatUnitClass } from '../components/GameTerms';
import { showStrandDiscoveryModal, parseDiscoveredStrands } from '../components/StrandDiscoveryModal';
import { RosterPicker } from '../components/RosterPicker';
import { Tooltip } from '../components/Tooltip';
import { getFullSpriteUrl, PLAYER_SPRITES } from '../../combat/spriteConfig';
import type {
  UnitData,
  ConfigData,
  PlayerData,
  PartyUnit,
  MasteryDef,
} from '../../types';

type SoulloomState = 'selection' | 'ready' | 'animating' | 'result';

const BASIN_SPRITE_URL = `${import.meta.env.BASE_URL}sprites/processed/props/soulloom_basin.png`;
const PLACEHOLDER_SPRITE_URL = `${import.meta.env.BASE_URL}sprites/processed/props/strand_silhouette.png`;

const RARITY_LABELS: Record<string, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  unique: 'Unique',
};

export class SoulloomScreen extends Screen {
  readonly screenConfig: ScreenConfig = {
    id: 'soulloom',
    name: 'The Soulloom',
    persistent: false,
    needsPhaser: false,
  };

  private state: SoulloomState = 'selection';
  private slotA: PartyUnit | null = null;
  private slotB: PartyUnit | null = null;
  private resultUnit: PartyUnit | null = null;
  private bonusAffix = false;
  private inputSnapshotA: PartyUnit | null = null;
  private inputSnapshotB: PartyUnit | null = null;

  constructor() {
    super({ rootId: 'soulloom-screen-root' });
  }

  createDOM(container: HTMLElement): void {
    const root = document.createElement('div');
    root.id = 'soulloom-screen-root';
    root.innerHTML = `
      <div class="soulloom-screen">
        ${screenHeaderHTML('The Soulloom')}
        <div class="sf-body">
          <div class="sf-stage" style="background-image:url('${import.meta.env.BASE_URL}sprites/processed/hub_bg/the_long_vigil_dormant.webp')">
            <div class="sf-stage-vignette"></div>
            <!-- Pair-hint chip above basin (when both filled) -->
            <div class="sf-pair-hint"></div>

            <!-- Side stat panels — sit on the OUTER side of each strand,
                 leaving the slot itself free for a full-size sprite. -->
            <div class="sf-side-panel sf-side-panel--left" data-slot="a"></div>
            <div class="sf-side-panel sf-side-panel--right" data-slot="b"></div>

            <!-- Two strand slots — uses the canonical StrandShowcase pattern
                 (same as home + crafting). Slot click opens the picker. -->
            <div class="sf-stage-slot sf-stage-slot--left" data-slot="a"></div>
            <div class="sf-stage-slot sf-stage-slot--right" data-slot="b"></div>

            <!-- The basin — sprite + glow. The basin IS the CTA. Cost shown
                 via tooltip + the confirmation modal — no visible label. -->
            <div class="sf-basin" data-state="idle" role="button" tabindex="0" aria-label="Soulloom basin">
              <div class="sf-basin-glow"></div>
              <img class="sf-basin-sprite" src="${BASIN_SPRITE_URL}" alt="" draggable="false" />
              <div class="sf-basin-flash"></div>
            </div>

            <!-- Result slot — center, hidden until result phase. -->
            <div class="sf-stage-slot sf-stage-slot--center hidden" data-slot="r">
              <div class="sf-stage-portrait"></div>
              <div class="sf-stage-card sf-stage-card--result"></div>
            </div>

            <!-- Result CTA — Weave Again, only shown post-result. -->
            <div class="sf-result-actions hidden">
              <button class="sf-done-btn" type="button">Weave Again</button>
            </div>
          </div>
        </div>
      </div>
    `;
    container.appendChild(root);
  }

  protected cacheElements(): void {}

  protected bindEvents(): void {
    this.$('.sf-stage-slot--left')?.addEventListener('click', () => this.onSlotClick('a'));
    this.$('.sf-stage-slot--right')?.addEventListener('click', () => this.onSlotClick('b'));
    const basin = this.$('.sf-basin');
    basin?.addEventListener('click', () => this.onBasinClick());
    basin?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
        e.preventDefault();
        this.onBasinClick();
      }
    });
    if (basin) Tooltip.attach(basin as HTMLElement, () => this.basinTooltipContent());
    this.rootEl?.querySelector('.sf-done-btn')?.addEventListener('click', () => this.onEnter());
  }

  /** Tooltip content reflecting current basin state. Replaces the visible
      "Weave · 1 Soulthread" label per UX call — confirmation modal carries
      the actual disclosure on tap. */
  private basinTooltipContent(): string {
    const player = StateManager.get('playerData') as PlayerData | undefined;
    const have = player?.soulthread ?? 0;
    const bothFilled = !!(this.slotA && this.slotB);

    if (this.state === 'animating' || this.state === 'result') {
      return `<div class="tooltip-inner"><div class="tooltip-title">The Soulloom</div></div>`;
    }
    if (!bothFilled) {
      return `<div class="tooltip-inner">
        <div class="tooltip-title">The Soulloom</div>
        <div class="tooltip-line">Weave two strands into one new strand.</div>
        <div class="tooltip-line tooltip-line--meta">Cost: 1 Soulthread &middot; you have ${have}</div>
      </div>`;
    }
    if (have < 1) {
      return `<div class="tooltip-inner">
        <div class="tooltip-title">No Soulthread</div>
        <div class="tooltip-line">You need 1 Soulthread to weave.</div>
      </div>`;
    }
    return `<div class="tooltip-inner">
      <div class="tooltip-title">Weave</div>
      <div class="tooltip-line">Fuse the two selected strands into one new strand.</div>
      <div class="tooltip-line tooltip-line--meta">Cost: 1 Soulthread &middot; you have ${have}</div>
    </div>`;
  }

  protected setupSubscriptions(): void {
    this.subscribeState('unitData', () => {
      if (this.state === 'selection' || this.state === 'ready') this.onRender();
    });
    this.subscribeState('playerData', () => {
      this.renderCurrencies();
      this.renderBasin();
    });
  }

  onEnter(): void {
    this.state = 'selection';
    this.slotA = null;
    this.slotB = null;
    this.resultUnit = null;
    this.bonusAffix = false;
    this.inputSnapshotA = null;
    this.inputSnapshotB = null;

    // Reset all transient classes from a previous merge animation.
    const rootClasses = ['is-converging', 'is-dissolving', 'is-flashing', 'is-emerging'];
    rootClasses.forEach(cls => this.rootEl?.classList.remove(cls));
    this.rootEl?.querySelectorAll('.sf-stage-slot').forEach(el => el.classList.remove(...rootClasses, 'is-spent'));
    this.$('.sf-stage-slot--center')?.classList.add('hidden');
    this.$('.sf-result-actions')?.classList.add('hidden');

    this.onRender();
  }

  protected onRender(): void {
    this.renderCurrencies();
    this.renderSlot('.sf-stage-slot--left', this.slotA);
    this.renderSlot('.sf-stage-slot--right', this.slotB);
    this.renderSidePanel('.sf-side-panel--left', this.slotA);
    this.renderSidePanel('.sf-side-panel--right', this.slotB);
    this.renderBasin();
    this.renderPairHint();
  }

  // ─── Helpers ───

  private getTopMasteries(unit: PartyUnit, count = 2): { id: string; rank: number; icon: string; name: string }[] {
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const ranks: Record<string, number> = unit.computedStats?.masteryRanks || {};
    const masteryDefs = configData?.masteries || {};

    return Object.entries(ranks)
      .filter(([, r]) => r > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, count)
      .map(([id, rank]) => ({
        id,
        rank,
        icon: (masteryDefs[id] as MasteryDef | undefined)?.icon ?? '',
        name: (masteryDefs[id] as MasteryDef | undefined)?.name ?? id,
      }));
  }

  private renderCurrencies(): void {
    if (!this.rootEl) return;
    // Soulloom only consumes Soulthread — filter the header to just that.
    renderScreenCurrencies(this.rootEl, 'soulloom', ['soulthread']);
  }

  private rarityLabel(rarity: string | undefined): string {
    return RARITY_LABELS[rarity ?? 'common'] ?? 'Common';
  }

  // ─── Stage slots ───

  private renderSlot(slotSelector: string, unit: PartyUnit | null): void {
    const slotEl = this.$(slotSelector);
    if (!slotEl) return;

    if (!unit) {
      slotEl.classList.remove('sf-stage-slot--filled');
      slotEl.classList.add('sf-stage-slot--empty');
      slotEl.innerHTML = `
        <img class="sf-stage-portrait-img sf-stage-portrait-img--empty"
             src="${PLACEHOLDER_SPRITE_URL}"
             alt="" draggable="false" />
      `;
      return;
    }

    slotEl.classList.add('sf-stage-slot--filled');
    slotEl.classList.remove('sf-stage-slot--empty');

    const configData = StateManager.get('configData') as ConfigData | undefined;
    slotEl.innerHTML = renderStrandShowcase({ unit, configData });
  }

  /** Side stat panel — sits on the OUTER side of each strand. Shows the full
      strand identity + masteries + ALL affixes when filled, hidden when empty. */
  private renderSidePanel(panelSelector: string, unit: PartyUnit | null): void {
    const el = this.$(panelSelector);
    if (!el) return;

    if (!unit) {
      el.classList.remove('is-visible');
      el.innerHTML = '';
      return;
    }

    el.classList.add('is-visible');
    const configData = StateManager.get('configData') as ConfigData | undefined;
    el.innerHTML = this.renderStrandCardBody(unit, configData, { showAllExplicits: true });
  }

  // Shared between stage slots and the result card. `explicitsLimit` controls
  // how many explicit affixes are listed (1 for stage slot, all for result).
  private renderStrandCardBody(
    unit: PartyUnit,
    configData: ConfigData | undefined,
    opts: { explicitsLimit?: number; showAllExplicits?: boolean },
  ): string {
    const rColor = rarityColor(unit.rarity ?? 'common');
    const className = formatUnitClass(unit.unitClass);
    const tier = this.rarityLabel(unit.rarity);
    const masteries = this.getTopMasteries(unit, opts.showAllExplicits ? 4 : 2);
    const masteryHTML = masteries.length
      ? `<div class="sf-stage-card-masteries">${masteries.map(m =>
          `<span class="sf-mastery-tag" title="${m.name}">${renderMasteryIcon(m.id)} ${m.rank}</span>`
        ).join('')}</div>`
      : '';

    const parsed = parseUnitAffixes(unit, configData);
    const affixLines: string[] = [];
    if (parsed.implicit) {
      const val = formatAffixValue(parsed.implicit.instance, parsed.implicit.def, configData);
      affixLines.push(`<div class="affix-line ${affixTypeClass(parsed.implicit.def)}">${val}</div>`);
    }
    const explicitsToShow = opts.showAllExplicits
      ? parsed.explicits
      : parsed.explicits.slice(0, opts.explicitsLimit ?? 1);
    for (const ex of explicitsToShow) {
      const val = formatAffixValue(ex.instance, ex.def, configData);
      const tierLabel = opts.showAllExplicits ? getTierLabel(ex.def, ex.instance.tier ?? 0) : '';
      affixLines.push(`<div class="affix-line ${affixTypeClass(ex.def)}">${val}${tierLabel ? ` <span class="affix-name">${tierLabel}</span>` : ''}</div>`);
    }
    const remaining = parsed.explicits.length - explicitsToShow.length;
    if (remaining > 0 && !opts.showAllExplicits) {
      affixLines.push(`<div class="affix-line affix-overflow">+${remaining} more</div>`);
    }
    const affixHTML = affixLines.length
      ? `<div class="sf-stage-card-affixes affix-list">${affixLines.join('')}</div>`
      : '';

    return `
      <div class="sf-stage-card-name" style="color: ${rColor}">${unit.name}</div>
      <div class="sf-stage-card-meta">Lv.${unit.level} ${className} · <span class="sf-stage-card-tier" style="color: ${rColor}">${tier}</span></div>
      ${masteryHTML ? `<div class="sf-stage-card-divider"></div>${masteryHTML}` : ''}
      ${affixHTML ? `<div class="sf-stage-card-divider"></div>${affixHTML}` : ''}
    `;
  }

  // ─── Basin (the CTA) ───

  private renderBasin(): void {
    const basin = this.$('.sf-basin');
    if (!basin) return;

    const player = StateManager.get('playerData') as PlayerData | undefined;
    const hasSoulthread = (player?.soulthread ?? 0) >= 1;
    const bothFilled = !!(this.slotA && this.slotB);

    let stateAttr: string;
    if (this.state === 'animating') stateAttr = 'firing';
    else if (this.state === 'result') stateAttr = 'spent';
    else if (!bothFilled) stateAttr = 'idle';
    else if (!hasSoulthread) stateAttr = 'disabled';
    else stateAttr = 'armed';

    basin.setAttribute('data-state', stateAttr);
  }

  // ─── Pair hint (input class hint above basin) ───

  private renderPairHint(): void {
    const hint = this.$('.sf-pair-hint');
    if (!hint) return;
    if (!this.slotA || !this.slotB || this.state !== 'ready') {
      hint.classList.remove('is-visible');
      hint.innerHTML = '';
      return;
    }
    const a = formatUnitClass(this.slotA.unitClass);
    const b = formatUnitClass(this.slotB.unitClass);
    hint.innerHTML = `<span>${a}</span><span class="sf-pair-x">×</span><span>${b}</span><span class="sf-pair-arrow">→</span><span class="sf-pair-q">?</span>`;
    hint.classList.add('is-visible');
  }

  // ─── Click handlers ───

  private onSlotClick(slot: 'a' | 'b'): void {
    if (this.state !== 'selection' && this.state !== 'ready') return;
    this.openPickerFor(slot);
  }

  private onBasinClick(): void {
    if (this.state !== 'selection' && this.state !== 'ready') return;
    if (!this.slotA || !this.slotB) return;
    const player = StateManager.get('playerData') as PlayerData | undefined;
    if ((player?.soulthread ?? 0) < 1) return;
    this.openConfirmModal();
  }

  // ─── Destructive-action confirmation ───────────────────────────────────
  // Soulloom is irreversible (destroys both inputs). Always ask before firing.
  private openConfirmModal(): void {
    if (!this.slotA || !this.slotB) return;
    const a = this.slotA;
    const b = this.slotB;
    const aName = a.name;
    const bName = b.name;
    const aClass = formatUnitClass(a.unitClass);
    const bClass = formatUnitClass(b.unitClass);
    const aRarity = this.rarityLabel(a.rarity);
    const bRarity = this.rarityLabel(b.rarity);
    const aColor = rarityColor(a.rarity ?? 'common');
    const bColor = rarityColor(b.rarity ?? 'common');

    const overlay = document.createElement('div');
    overlay.className = 'sf-confirm-overlay';
    overlay.innerHTML = `
      <div class="sf-confirm-backdrop"></div>
      <div class="sf-confirm-panel">
        <div class="sf-confirm-title">Weave these strands?</div>
        <div class="sf-confirm-body">
          <div class="sf-confirm-line">
            <span class="sf-confirm-name" style="color:${aColor}">${aName}</span>
            <span class="sf-confirm-meta">Lv.${a.level} ${aClass} · ${aRarity}</span>
          </div>
          <div class="sf-confirm-and">+</div>
          <div class="sf-confirm-line">
            <span class="sf-confirm-name" style="color:${bColor}">${bName}</span>
            <span class="sf-confirm-meta">Lv.${b.level} ${bClass} · ${bRarity}</span>
          </div>
          <div class="sf-confirm-warning">
            Both strands will be permanently destroyed and replaced with one new strand of unknown class.
          </div>
        </div>
        <div class="sf-confirm-actions">
          <button class="sf-confirm-cancel" type="button">Cancel</button>
          <button class="sf-confirm-go" type="button">Weave · 1 Soulthread</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('is-visible'));

    const close = () => {
      overlay.classList.remove('is-visible');
      document.removeEventListener('keydown', onKey);
      setTimeout(() => overlay.remove(), 300);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'Enter') { close(); this.forge(); }
    };
    overlay.querySelector('.sf-confirm-cancel')?.addEventListener('click', close);
    overlay.querySelector('.sf-confirm-backdrop')?.addEventListener('click', close);
    overlay.querySelector('.sf-confirm-go')?.addEventListener('click', () => {
      close();
      this.forge();
    });
    document.addEventListener('keydown', onKey);
  }

  private openPickerFor(slot: 'a' | 'b'): void {
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const configData = StateManager.get('configData') as ConfigData | undefined;
    if (!unitData) return;

    const opposite = slot === 'a' ? this.slotB : this.slotA;
    const current = slot === 'a' ? this.slotA : this.slotB;
    const available = unitData.roster.filter(u => !u.equipped);
    if (available.length === 0) return;

    RosterPicker.open({
      title: opposite ? 'Pair with Strand' : 'Select Strand',
      units: available,
      pairingWith: opposite,
      classFilter: true,
      isDisabled: (unit) => {
        if (opposite && unit.id === opposite.id) return 'Already in opposite slot';
        return false;
      },
      cardClasses: (unit) => current && unit.id === current.id ? 'roster-card--selected' : '',
      renderCard: (unit, disabled) => this.renderSoulloomCard(unit, configData, disabled),
      onPick: (unit) => this.fillSlot(slot, unit),
    });
  }

  private renderSoulloomCard(
    unit: PartyUnit,
    configData: ConfigData | undefined,
    disabled: string | false,
  ): string {
    const template = configData?.unitTemplates?.[unit.unitClass];
    const skinKey = unit.activeSkinId && configData?.skins?.[unit.activeSkinId]?.spriteKey;
    const spriteKey = skinKey || template?.spriteKey || unit.unitClass;
    const spriteSrc = getFullSpriteUrl(spriteKey);
    const rColor = rarityColor(unit.rarity ?? 'common');
    const className = formatUnitClass(unit.unitClass);
    const top = this.getTopMasteries(unit, 2);
    const masteryHtml = top.length > 0
      ? `<div class="roster-card-masteries">${top.map(m =>
          `<span class="sf-mastery-tag">${renderMasteryIcon(m.id)} ${m.rank}</span>`
        ).join('')}</div>`
      : '';

    const parsed = parseUnitAffixes(unit, configData);
    let topAffixHtml = '';
    if (parsed.implicit) {
      const val = formatAffixValue(parsed.implicit.instance, parsed.implicit.def, configData);
      topAffixHtml = `<div class="roster-card-affix ${affixTypeClass(parsed.implicit.def)}">${val}</div>`;
    } else if (parsed.explicits.length > 0) {
      const ex = parsed.explicits[0];
      const val = formatAffixValue(ex.instance, ex.def, configData);
      topAffixHtml = `<div class="roster-card-affix ${affixTypeClass(ex.def)}">${val}</div>`;
    }

    const disabledLabel = typeof disabled === 'string'
      ? `<span class="roster-card-pill roster-card-pill--locked">${disabled}</span>`
      : '';

    return `
      ${disabledLabel}
      <div class="roster-card-art">
        <img class="roster-card-sprite" src="${spriteSrc}" alt="${unit.name}" draggable="false">
      </div>
      <div class="roster-card-info">
        ${masteryHtml}
        <span class="roster-card-name" style="color:${rColor}">${unit.name}</span>
        <span class="roster-card-meta">Lv.${unit.level} ${className}</span>
        ${topAffixHtml}
      </div>
    `;
  }

  private fillSlot(slot: 'a' | 'b', unit: PartyUnit): void {
    if (slot === 'a') this.slotA = unit;
    else this.slotB = unit;
    this.state = (this.slotA && this.slotB) ? 'ready' : 'selection';
    this.onRender();
  }

  // ─── Forge — drives the actual stage sprites, no overlay layer ───

  private async forge(): Promise<void> {
    if (!this.slotA || !this.slotB) return;

    const playerSnap = StateManager.get('playerData') as PlayerData | undefined;
    const preFusionDiscovered = parseDiscoveredStrands(playerSnap);

    this.state = 'animating';
    this.renderBasin();         // basin → firing
    this.renderPairHint();      // hide pair hint

    this.inputSnapshotA = { ...this.slotA };
    this.inputSnapshotB = { ...this.slotB };

    const left = this.$('.sf-stage-slot--left');
    const right = this.$('.sf-stage-slot--right');
    const basin = this.$('.sf-basin');
    const flash = this.$('.sf-basin-flash');

    // Fire API call early in parallel with the converging animation.
    const apiPromise = APIManager.soulloomRecombine(this.slotA.id, this.slotB.id);

    // Phase 1: converge (sprites slide toward basin, slight fade)
    left?.classList.add('is-converging');
    right?.classList.add('is-converging');
    await this.wait(620);

    // Phase 2: dissolve into basin
    left?.classList.add('is-dissolving');
    right?.classList.add('is-dissolving');
    await this.wait(480);

    // Wait for API
    const result = await apiPromise;
    if (!result.success || !result.data) {
      console.error('[Soulloom] Recombine failed:', result.error);
      // Reset cleanly
      left?.classList.remove('is-converging', 'is-dissolving');
      right?.classList.remove('is-converging', 'is-dissolving');
      this.state = 'selection';
      this.slotA = null;
      this.slotB = null;
      this.onRender();
      return;
    }

    // Phase 3: basin flash + result emerges
    basin?.classList.add('is-firing');
    flash?.classList.add('is-flashing');
    await this.wait(220);
    flash?.classList.remove('is-flashing');

    const outputUnit = result.data.outputUnit as PartyUnit;
    this.resultUnit = outputUnit;
    this.bonusAffix = result.data.bonusAffixAdded;

    // Hide spent input slots — they've dissolved.
    left?.classList.add('is-spent');
    right?.classList.add('is-spent');

    // Render result strand into center slot (initially hidden), then trigger
    // emerge animation.
    const configData = StateManager.get('configData') as ConfigData | undefined;
    this.renderResultSlot(outputUnit, configData);
    const center = this.$('.sf-stage-slot--center');
    center?.classList.remove('hidden');
    requestAnimationFrame(() => center?.classList.add('is-emerging'));

    await this.wait(1100);
    basin?.classList.remove('is-firing');

    // Refresh state
    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        playerData: stateResult.data.playerData,
        unitData: stateResult.data.unitData,
        itemData: stateResult.data.itemData,
      });
    }

    // Reveal Weave Again CTA
    this.$('.sf-result-actions')?.classList.remove('hidden');
    requestAnimationFrame(() => this.$('.sf-result-actions')?.classList.add('is-visible'));

    this.state = 'result';
    this.renderBasin();

    if (this.resultUnit && !preFusionDiscovered.has(this.resultUnit.unitClass)) {
      showStrandDiscoveryModal(this.resultUnit.unitClass).catch(err => {
        console.warn('[Soulloom] Strand discovery modal failed:', err);
      });
    }
  }

  private renderResultSlot(unit: PartyUnit, configData: ConfigData | undefined): void {
    const slot = this.$('.sf-stage-slot--center');
    if (!slot) return;
    const portrait = slot.querySelector('.sf-stage-portrait') as HTMLElement | null;
    const card = slot.querySelector('.sf-stage-card') as HTMLElement | null;
    if (!portrait || !card) return;

    const template = configData?.unitTemplates?.[unit.unitClass];
    const spriteKey = template?.spriteKey ?? unit.unitClass;
    portrait.innerHTML = renderUnitPortrait({
      spriteKey,
      name: unit.name,
      size: 'lg',
      scale: PLAYER_SPRITES[spriteKey]?.scale,
    });

    card.innerHTML = this.renderResultSummaryCard(unit, configData);
    if (this.bonusAffix) card.classList.add('sf-stage-card--bonus');
    else card.classList.remove('sf-stage-card--bonus');
  }

  /** Comprehensive merge-summary card. Sits ABOVE the result strand sprite.
      Shows: WOVEN FROM section (consumed inputs), then the result strand's
      full identity + masteries + all affixes. */
  private renderResultSummaryCard(unit: PartyUnit, configData: ConfigData | undefined): string {
    const rColor = rarityColor(unit.rarity ?? 'common');
    const className = formatUnitClass(unit.unitClass);
    const tier = this.rarityLabel(unit.rarity);

    // ─── Top: WOVEN FROM (consumed inputs) ───
    const inputsHTML = (this.inputSnapshotA && this.inputSnapshotB)
      ? this.renderConsumedInputs(this.inputSnapshotA, this.inputSnapshotB, configData)
      : '';

    // ─── Result identity ───
    const identityHTML = `
      <div class="sf-summary-identity">
        <div class="sf-summary-name" style="color:${rColor}">${unit.name}</div>
        <div class="sf-summary-meta">Lv.${unit.level} ${className} · <span style="color:${rColor}">${tier}</span></div>
      </div>
    `;

    // ─── Masteries (top 4) ───
    const masteries = this.getTopMasteries(unit, 4);
    const masteryHTML = masteries.length
      ? `<div class="sf-summary-row sf-summary-masteries">${masteries.map(m =>
          `<span class="sf-mastery-tag" title="${m.name}">${renderMasteryIcon(m.id)} ${m.rank}</span>`
        ).join('')}</div>`
      : '';

    // ─── Affixes (implicit + ALL explicits with tier labels) ───
    const parsed = parseUnitAffixes(unit, configData);
    const affixLines: string[] = [];
    if (parsed.implicit) {
      const val = formatAffixValue(parsed.implicit.instance, parsed.implicit.def, configData);
      affixLines.push(`<div class="affix-line ${affixTypeClass(parsed.implicit.def)}">${val} <span class="affix-name">(${parsed.implicit.def.name})</span></div>`);
    }
    for (let i = 0; i < parsed.explicits.length; i++) {
      const ex = parsed.explicits[i];
      const val = formatAffixValue(ex.instance, ex.def, configData);
      const tierLabel = getTierLabel(ex.def, ex.instance.tier ?? 0);
      const isBonus = this.bonusAffix && i === parsed.explicits.length - 1;
      affixLines.push(`<div class="affix-line ${affixTypeClass(ex.def)}${isBonus ? ' affix-bonus' : ''}">${val} <span class="affix-name">(${ex.def.name}${tierLabel})</span></div>`);
    }
    const affixHTML = affixLines.length
      ? `<div class="sf-summary-row sf-summary-affixes affix-list">${affixLines.join('')}</div>`
      : '';

    const bonusBadge = this.bonusAffix
      ? `<div class="sf-summary-bonus">+1 Bonus Affix Rolled</div>`
      : '';

    return `
      ${inputsHTML}
      <div class="sf-summary-divider"></div>
      ${identityHTML}
      ${masteryHTML ? `<div class="sf-summary-divider sf-summary-divider--soft"></div>${masteryHTML}` : ''}
      ${affixHTML ? `<div class="sf-summary-divider sf-summary-divider--soft"></div>${affixHTML}` : ''}
      ${bonusBadge}
    `;
  }

  private renderConsumedInputs(a: PartyUnit, b: PartyUnit, configData: ConfigData | undefined): string {
    const renderChip = (unit: PartyUnit) => {
      const template = configData?.unitTemplates?.[unit.unitClass];
      const spriteKey = template?.spriteKey ?? unit.unitClass;
      const spriteSrc = getFullSpriteUrl(spriteKey);
      const rColor = rarityColor(unit.rarity ?? 'common');
      const className = formatUnitClass(unit.unitClass);
      const tier = this.rarityLabel(unit.rarity);
      return `
        <div class="sf-summary-input">
          <img class="sf-summary-input-sprite" src="${spriteSrc}" alt="${unit.name}" draggable="false">
          <div class="sf-summary-input-text">
            <span class="sf-summary-input-name" style="color:${rColor}">${unit.name}</span>
            <span class="sf-summary-input-meta">Lv.${unit.level} ${className} · ${tier}</span>
          </div>
        </div>
      `;
    };

    return `
      <div class="sf-summary-section">
        <div class="sf-summary-section-label">Woven From</div>
        <div class="sf-summary-inputs">
          ${renderChip(a)}
          <span class="sf-summary-input-plus">+</span>
          ${renderChip(b)}
        </div>
      </div>
    `;
  }

  private wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
